import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';

/**
 * Creates a new product with the provided data.
 * @param {Object} payload - The data for the new product.
 * @returns {Promise<Object>} A promise that resolves with the created product data.
 * @throws Will throw an error if the creation request fails.
 */
export const createNewProduct = async (payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.post('/products/create', payload, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to create a new product:', error);
    throw error;
  }
};

/**
 * Retrieves all products.
 * @returns {Promise<Object[]>} A promise that resolves with an array of product objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllProducts = async (params) => {
  const filters = prepareFilters(params, {
    company: 'default',
    title: 'default',
    description: 'default',
    unit: 'default',
    priceEUR: 'default',
    priceRSD: 'default',
    createdAt: 'range',
    updatedAt: 'range',
  });

  const sorters = prepareSorters(params, {
    title: 'sortTitle',
    description: 'sortDescription',
    unit: 'sortUnit',
    priceEUR: 'sortPriceEUR',
    priceRSD: 'sortPriceRSD',
    createdAt: 'sortCreatedAt',
    updatedAt: 'sortUpdatedAt',
  });

  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/products', {
      params: {
        filter: filters,
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve products:', error);
    throw error;
  }
};

/**
 * Updates an existing product by its ID with the provided data.
 * @param {string} productId - The unique identifier of the product.
 * @param {Object} payload - The updated data for the product.
 * @returns {Promise<Object>} A promise that resolves with the updated product data.
 * @throws Will throw an error if the update request fails.
 */
export const updateProductById = async (productId, payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.put(`/products/${productId}`, payload, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error(`Failed to update product with ID ${productId}:`, error);
    throw error;
  }
};

/**
 * Retrieves a product by their ID.
 * @param {string} productId - The unique identifier of the product.
 * @returns {Promise<Object>} A promise that resolves with the product data.
 * @throws Will throw an error if the request fails.
 */
export const getProductById = async (productId) => {
  try {
    const response = await axiosClient.get(`/products/${productId}`);
    const { data } = response.data;
    return data;
  } catch (error) {
    console.error(`Failed to retrieve product with ID ${productId}:`, error);
    throw error;
  }
};

/**
 * Deletes a product by their ID.
 * @param {string} productId - The unique identifier of the product.
 * @returns {Promise<Object>} A promise that resolves when the product is deleted.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteProductById = async (productId) => {
  try {
    const response = await axiosClient.delete(`/products/${productId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete product with ID ${productId}:`, error);
    throw error;
  }
};

/**
 * Retrieves lightweight products for dropdowns (id/label + key fields).
 * Backend expects `filter.company` (ObjectId as string) and optional `q` for text search.
 *
 * @param {Object} params
 * @param {string} params.company - Company (ObjectId as string). Required.
 * @param {string} [params.q]     - Optional search text. When empty, backend returns the first page.
 * @param {number} [params.limit] - Max items to return (default 20).
 * @returns {Promise<{items: Array}>}
 * @throws Will throw an error if the request fails.
 */
export const retrieveDropdownAllProducts = async (params = {}) => {
  const company = params.company ?? params.from ?? undefined;
  const q = typeof params.q === 'string' ? params.q : '';
  const limit = Number.isFinite(Number(params.limit)) ? Number(params.limit) : 20;

  if (!company) return { items: [] };

  const filters = { company };

  try {
    const response = await axiosClient.get('/products/dropdown', {
      params: {
        filter: filters,
        q,
        limit,
      },
      paramsSerializer: (p) => {
        if (p.filter) p.filter = JSON.stringify(p.filter);
        return qs.stringify(p, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve products for dropdown:', error);
    throw error;
  }
};

const ProductsService = {
  retrieveAllProducts,
  createNewProduct,
  updateProductById,
  getProductById,
  deleteProductById,
  retrieveDropdownAllProducts,
};

export default ProductsService;
